import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUrl';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export function usePublishedContent(endpoint) {
  const [state, setState] = useState({ data: null, error: false, notFound: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ data: null, error: false, notFound: false });
    axios.get(`${API}/${endpoint}`, { timeout: 15000 }).then(({ data }) => {
      if (active) setState({ data, error: false });
    }).catch(error => {
      if (active) setState({ data: null, error: true, notFound: error.response?.status === 404 });
    });
    return () => { active = false; };
  }, [endpoint, attempt]);
  return { ...state, retry: () => setAttempt(a => a + 1) };
}

function ContentState({ state, label, children }) {
  if (state.error) return <div className="content-status" role="alert">We couldn’t load {label}. <button className="text-link" onClick={state.retry}>Try again</button><p>You can still <a href="/estimate">request an estimate</a> or <a href="tel:+17724945048">call our crew</a>.</p></div>;
  if (state.data === null) return <p className="content-status" role="status">Loading {label}…</p>;
  return children;
}

export function ProjectCard({ item }) {
  const [split, setSplit] = useState(50);
  const after = resolveImageUrl(item.after_image_url || item.before_image_url);
  const before = resolveImageUrl(item.before_image_url);
  const compare = item.is_before_after && before && item.after_image_url;
  return <article className="project-card">
    <div className="comparison" style={{ '--split': `${split}%` }}>
      <img src={after} alt={`After: ${item.title}`} loading="lazy" width="900" height="650" />
      {compare && <><div className="before-layer"><img src={before} alt={`Before: ${item.title}`} loading="lazy" width="900" height="650" /></div>
        <span className="before-label">BEFORE</span><span className="after-label">AFTER</span><div className="compare-line" aria-hidden="true"><i>↔</i></div>
        <input type="range" min="0" max="100" value={split} onChange={e => setSplit(Number(e.target.value))} aria-label={`Compare before and after for ${item.title}`} /></>}
    </div>
    <div className="project-copy"><span className="eyebrow">{(item.tags || []).join(' / ')}</span><h3>{item.title}</h3><p>{item.description}</p></div>
  </article>;
}

export function Gallery({ preview = false }) {
  const state = usePublishedContent('gallery');
  const [filter, setFilter] = useState('all');
  const all = Array.isArray(state.data) ? state.data : [];
  const filters = [['all', 'All projects'], ['furniture', 'Furniture'], ['garage', 'Garages'], ['yard|debris|fence|construction', 'Yard & debris'], ['cleanout', 'Cleanouts'], ['appliance', 'Appliances']];
  const visible = all.filter(item => filter === 'all' || new RegExp(filter, 'i').test(`${item.title} ${(item.tags || []).join(' ')}`));
  return <ContentState state={state} label="projects">
    {!preview && all.length > 0 && <div className="filter-bar" role="group" aria-label="Filter projects">{filters.map(([value, label]) => <button key={value} className={`filter-chip ${filter === value ? 'active' : ''}`} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>)}</div>}
    <div className="project-grid">{(preview ? visible.slice(0, 2) : visible).map(item => <ProjectCard key={item.id} item={item} />)}</div>
    {visible.length === 0 && <p className="content-status">No projects to show yet. Check back soon.</p>}
  </ContentState>;
}

export function ReviewCard({ item, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const changeOpen = value => { setOpen(value); onOpenChange?.(value); };
  const rating = Math.max(0, Math.min(5, Number(item.rating) || 0));
  return <>
    <article className="review-card">
      <div className="review-top"><span className="stars" aria-label={`${rating} out of 5 stars`}>{'★'.repeat(rating)}</span><span>{item.source}</span></div>
      <blockquote className="review-excerpt">{item.text}</blockquote>
      <button className="review-read-more" onClick={() => changeOpen(true)} aria-label={`Read full review by ${item.author_name}`}>Read full review ↗</button>
      <div className="review-author"><i aria-hidden="true">{item.author_name?.charAt(0)}</i><div><strong>{item.author_name}</strong><span>{item.author_location}</span></div></div>
    </article>
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="review-dialog">
        <DialogTitle>Review by {item.author_name}</DialogTitle>
        <DialogDescription>{item.author_location}{item.source ? ` · ${item.source}` : ''}</DialogDescription>
        <span className="stars" aria-label={`${rating} out of 5 stars`}>{'★'.repeat(rating)}</span>
        <blockquote>{item.text}</blockquote>
      </DialogContent>
    </Dialog>
  </>;
}

export function ReviewCarousel({ items }) {
  const [offset, setOffset] = useState(0);
  const [paused, setPaused] = useState(() => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pageSize, setPageSize] = useState(() => window.matchMedia?.('(max-width: 700px)').matches ? 1 : 3);
  const count = Math.min(pageSize, items.length);
  const canRotate = items.length > count;
  const stopped = paused || hovered || focused || dialogOpen;
  useEffect(() => {
    const mobile = window.matchMedia?.('(max-width: 700px)');
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const resize = () => setPageSize(mobile?.matches ? 1 : 3);
    const reduce = () => { if (motion?.matches) setPaused(true); };
    mobile?.addEventListener?.('change', resize);
    motion?.addEventListener?.('change', reduce);
    return () => { mobile?.removeEventListener?.('change', resize); motion?.removeEventListener?.('change', reduce); };
  }, []);
  useEffect(() => {
    if (!canRotate || stopped) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setOffset(value => (value + count) % items.length);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [canRotate, stopped, count, items.length]);
  const shown = Array.from({ length: count }, (_, i) => items[(offset + i) % items.length]);
  const move = direction => setOffset(value => (value + direction * count + items.length) % items.length);
  return <section className="review-carousel" aria-label="Customer reviews" aria-roledescription="carousel"
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
    <div className="reviews-grid" aria-live="off">{shown.map(item => <ReviewCard key={item.id} item={item} onOpenChange={setDialogOpen} />)}</div>
    {canRotate && <div className="review-carousel-controls">
      <span>{stopped ? 'Paused for reading' : 'More neighbor stories every 10 seconds'}</span>
      <div className="review-carousel-buttons">
        <button type="button" onClick={() => move(-1)} aria-label="Previous reviews">←</button>
        <button type="button" onClick={() => setPaused(value => !value)} aria-pressed={paused} aria-label={paused ? 'Play reviews' : 'Pause reviews'}>{paused ? '▶ Play' : 'Ⅱ Pause'}</button>
        <button type="button" onClick={() => move(1)} aria-label="Next reviews">→</button>
      </div>
    </div>}
  </section>;
}

export function Reviews({ preview = false }) {
  const state = usePublishedContent('testimonials');
  const [query, setQuery] = useState('');
  const all = Array.isArray(state.data) ? state.data : [];
  const visible = all.filter(item => `${item.author_name} ${item.author_location} ${item.text}`.toLowerCase().includes(query.trim().toLowerCase()));
  return <ContentState state={state} label="reviews">
    {!preview && <label className="search-label">Find a review<input className="search-input" type="search" placeholder="Search by city, name, or job…" value={query} onChange={e => setQuery(e.target.value)} /></label>}
    {preview ? <ReviewCarousel items={visible} /> : <div className="reviews-grid">{visible.map(item => <ReviewCard key={item.id} item={item} />)}</div>}
    {visible.length === 0 && <p className="content-status" role="status">{query ? 'No reviews match your search.' : 'No reviews published yet.'}</p>}
  </ContentState>;
}

export function BlogCard({ post }) {
  return <a className="journal-card" href={`/blog/${encodeURIComponent(post.slug)}`}>
    <img src={resolveImageUrl(post.featured_image_url) || '/redesign/assets/heroes/blog.webp'} alt="" loading="lazy" width="900" height="600" />
    <div><span className="eyebrow">THE HAUL JOURNAL</span><h2>{post.title}</h2><p>{post.excerpt}</p><span className="text-link">Read the story ↗</span></div>
  </a>;
}

export function Blog() {
  const state = usePublishedContent('blog');
  const posts = Array.isArray(state.data) ? state.data : [];
  return <ContentState state={state} label="articles"><div className="journal-list">{posts.map(post => <BlogCard key={post.id} post={post} />)}{posts.length === 0 && <p>New articles are on the way. Check back soon.</p>}</div></ContentState>;
}

// The existing editor stores plain text with headings and lists, never executable HTML.
export function ArticleBody({ content = '' }) {
  return content.split(/\n\n+/).map((block, i) => {
    if (/^#{1,3}\s/.test(block)) return <h2 key={i}>{block.replace(/^#{1,3}\s+/, '')}</h2>;
    if (block.startsWith('- ')) return <ul key={i}>{block.split('\n').map((line, j) => <li key={j}>{line.replace(/^-\s*/, '')}</li>)}</ul>;
    return <p key={i}>{block}</p>;
  });
}

export function Article({ state }) {
  return <ContentState state={state} label="this article">{state.data && <article className="article-copy"><p className="eyebrow">{state.data.author || 'Junk In Da Truck'}{state.data.published_at && <> · <time dateTime={state.data.published_at}>{new Date(state.data.published_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'long', day: 'numeric' })}</time></>}</p><ArticleBody content={state.data.content} /></article>}</ContentState>;
}
